import {Icon, LabelRegular, LabelSmallMedium, TextSemiBold, clx} from '@hm/fundamental'
import {mdiChevronDown} from '@mdi/js'
import {useState} from 'react'

import {formatDateOnly, formatDateTime} from '../../helpers'
import {useRiskSurveyTranslation} from '../../hooks'
import type {RiaDetailsAuditLogEntryDto} from '../../types'

type RiaDetailsAuditLogSectionProps = {
  entries: RiaDetailsAuditLogEntryDto[]
}

export const RiaDetailsAuditLogSection = ({entries}: RiaDetailsAuditLogSectionProps) => {
  const {t, i18n} = useRiskSurveyTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const personLabel = (name?: string | null) => name || '—'
  const dateLabel = (date?: string | null) => (date ? formatDateOnly(date, i18n.language) : '—')

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between py-2"
        data-cy="details-audit-log-toggle"
      >
        <TextSemiBold>{t('detailsAuditLog.title')}</TextSemiBold>
        <span className={clx('transition-transform', isExpanded && 'rotate-180')}>
          <Icon path={mdiChevronDown} size="regular" />
        </span>
      </button>
      {isExpanded &&
        (entries.length === 0 ? (
          <LabelRegular className="text-foreground_faded">
            {t('detailsAuditLog.empty')}
          </LabelRegular>
        ) : (
          <div className="flex flex-col gap-3 pt-1">
            {entries.map((entry) => {
              // Migrated pre-user entries only carry names (former role labels), so compare both.
              const responsibleChanged =
                (entry.previousResponsiblePersonUserId ?? null) !==
                  (entry.newResponsiblePersonUserId ?? null) ||
                (entry.previousResponsiblePersonName ?? null) !==
                  (entry.newResponsiblePersonName ?? null)
              const dueDateChanged = entry.previousDueDate !== entry.newDueDate

              return (
                <div key={entry.id} className="pl-4 border-l-2 border-gray-300">
                  <div className="flex flex-col gap-1">
                    {responsibleChanged && (
                      <LabelRegular>
                        {t('detailsAuditLog.responsiblePerson')}:{' '}
                        {personLabel(entry.previousResponsiblePersonName)} →{' '}
                        {personLabel(entry.newResponsiblePersonName)}
                      </LabelRegular>
                    )}
                    {dueDateChanged && (
                      <LabelRegular>
                        {t('detailsAuditLog.dueDate')}: {dateLabel(entry.previousDueDate)} →{' '}
                        {dateLabel(entry.newDueDate)}
                      </LabelRegular>
                    )}
                  </div>
                  <LabelSmallMedium className="text-foreground_faded">
                    {entry.changedByUserName} · {formatDateTime(entry.changedAt, i18n.language)}
                  </LabelSmallMedium>
                </div>
              )
            })}
          </div>
        ))}
    </div>
  )
}
